// Timeline visual del journey de puja de un usuario
// Muestra todos los intentos + pujas confirmadas con badges de colores

import React from 'react';
import { ArrowRight } from 'lucide-react';
import { getAttemptStatusConfig, formatAmount as formatAmountUtil } from '../../../../utils/biddingAttemptsUtils';

/**
 * Formatear cantidad en euros (versión corta para badges)
 */
const formatAmount = (amount) => {
  if (!amount) return null;
  // Formato corto: €380k en lugar de €380.000
  if (amount >= 1000000) {
    return `€${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 1000) {
    return `€${Math.round(amount / 1000)}k`;
  }
  return `€${amount}`;
};

/**
 * Badge individual de intento/puja
 */
const JourneyBadge = ({ item, isLast = false }) => {
  const config = getAttemptStatusConfig(item.status);
  const amount = item.amounts?.[item.amounts.length - 1] || item.finalAmount || item.amount;
  
  // Determinar estilo según estado
  let bgClass, textClass, borderClass;
  
  switch (item.status) {
    case 'success':
      bgClass = 'bg-green-500/20';
      textClass = 'text-green-400';
      borderClass = 'border-green-500/30';
      break;
    case 'abandoned':
      bgClass = 'bg-amber-500/10';
      textClass = 'text-amber-400';
      borderClass = 'border-amber-500/30';
      break;
    case 'failed':
      bgClass = 'bg-red-500/20';
      textClass = 'text-red-300';
      borderClass = 'border-red-500/40';
      break;
    case 'expired':
      bgClass = 'bg-gray-500/10';
      textClass = 'text-gray-400';
      borderClass = 'border-gray-500/30';
      break;
    case 'pending_verification':
      bgClass = 'bg-blue-500/10';
      textClass = 'text-blue-400';
      borderClass = 'border-blue-500/30';
      break;
    default:
      bgClass = 'bg-slate-700';
      textClass = 'text-slate-400';
      borderClass = 'border-slate-600';
  }

  return (
    <div className="flex items-center gap-1">
      <div 
        className={`${bgClass} ${textClass} border ${borderClass} px-2 py-1 rounded text-xs font-medium flex items-center gap-1`}
        title={`${config.label}: ${item.reason || ''}`}
      >
        <span>{config.icon}</span>
        {amount && <span>{formatAmount(amount)}</span>}
      </div>
      {!isLast && (
        <ArrowRight className="w-3 h-3 text-slate-600 flex-shrink-0" />
      )}
    </div>
  );
};

/**
 * Timeline visual del journey completo
 * Combina intentos (del engagement) + pujas confirmadas (de bids)
 */
export const BidJourneyTimeline = ({ journey = [], bids = [] }) => {
  // Construir timeline unificado
  const combinedTimeline = [];

  // 1. Añadir todos los intentos del journey
  journey.forEach(attempt => {
    combinedTimeline.push({
      ...attempt,
      type: 'attempt',
      timestamp: new Date(attempt.startedAt),
      amount: attempt.amounts?.[attempt.amounts.length - 1] || attempt.finalAmount,
    });
  });

  // 2. Añadir pujas confirmadas que NO estén ya representadas en journey
  // Una puja está representada si hay un intento 'success' con el mismo amount
  const successAmounts = new Set(
    journey
      .filter(j => j.status === 'success')
      .map(j => j.finalAmount)
  );

  bids.forEach(bid => {
    // Solo añadir si esta cantidad NO está ya como intento exitoso
    if (!successAmounts.has(bid.amount)) {
      combinedTimeline.push({
        type: 'confirmed_bid',
        status: 'success',
        amount: bid.amount,
        finalAmount: bid.amount,
        reason: bid.is_last_chance ? 'Puja en última oportunidad' : 'Puja confirmada',
        timestamp: new Date(bid.timestamp || bid.created_at),
        isLastChance: bid.is_last_chance,
      });
    }
  });

  // Ordenar por timestamp
  combinedTimeline.sort((a, b) => a.timestamp - b.timestamp);

  if (combinedTimeline.length === 0) {
    return (
      <div className="text-slate-500 text-sm italic py-2">
        Sin actividad de puja registrada
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Timeline visual con badges */}
      <div className="flex items-center gap-1 flex-wrap">
        {combinedTimeline.map((item, idx) => (
          <JourneyBadge 
            key={idx} 
            item={item} 
            isLast={idx === combinedTimeline.length - 1}
          />
        ))}
      </div>

      {/* Leyenda compacta */}
      <div className="flex flex-wrap gap-3 text-xs text-slate-500 pt-2 border-t border-slate-700/50">
        <span className="flex items-center gap-1">
          <span className="text-green-400">✔</span> Confirmada
        </span>
        <span className="flex items-center gap-1">
          <span className="text-amber-400">🟡</span> Abandonado
        </span>
        <span className="flex items-center gap-1">
          <span className="text-red-300">❌</span> Error
        </span>
        <span className="flex items-center gap-1">
          <span className="text-blue-400">📧</span> Pendiente
        </span>
        <span className="flex items-center gap-1">
          <span className="text-orange-400">⏰</span> Expirado
        </span>
      </div>
    </div>
  );
};

export default BidJourneyTimeline;